import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, API_KEY_STORAGE } from '../api/client';
import { useAuth, useHealth, useTheme } from '../state';
import { Button, Checkbox, Tabs, TabsList, TabsTrigger } from '../components/ui';
import { TextField } from '../components/Form';
import { CopyButton } from '../components/CopyButton';
import { ErrorNotice } from '../components/Feedback';

/**
 * Signing in mints an API key, and the key *is* the session: the server only
 * ever shows the plaintext once. So the flow has three states, not two: form,
 * "here is your key, copy it now", and the app.
 */
export function LoginPage({
  onKeyIssued,
}: {
  onKeyIssued: (issued: { apiKey: string; scopes: readonly string[] }) => void;
}): JSX.Element {
  const auth = useAuth();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // The reveal is rendered above the auth gate (see App), because the moment a
  // key exists this component is unmounted: the session becomes real, and a
  // component that unmounts cannot show a one-time secret.
  if (auth.ready && auth.apiKey) return <Navigate to="/" replace />;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (mode === 'signup' && !agreeTerms) {
      setError(new Error('You must agree to the Terms of Service and Privacy Policy to create an account.'));
      return;
    }
    setPending(true);
    setError(null);
    try {
      const response =
        mode === 'signup'
          ? await auth.signUp(email, password, organizationName || undefined, agreeTerms)
          : await auth.signIn(email, password);
      // The server shows the plaintext exactly once; this is that moment.
      onKeyIssued({ apiKey: response.apiKey, scopes: response.scopes });
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__pitch">
        <h1>
          Justloop
          <span className="auth__version">agentic verification</span>
        </h1>
        <p className="auth__lead">
          AI writes code faster than anyone can prove it works. Justloop opens your real app in a real
          browser, uses it like a user, and hands your coding agent one self-consistent bundle describing
          exactly what broke, so it fixes its own work.
        </p>
        <ol className="auth__loop">
          <li>
            <strong>Write a plan</strong> in plain language: steps, assertions, no browser code.
          </li>
          <li>
            <strong>Run it</strong> against the real product in a real browser.
          </li>
          <li>
            <strong>Take one bundle</strong>: root cause, suggested fix, screenshot, everything captured
            under a single snapshot id.
          </li>
        </ol>
        <ServerIndicator />
      </div>

      <div className="auth__panel">
        <Tabs value={mode} onValueChange={(value) => { setMode(value as 'signin' | 'signup'); setError(null); }}>
          <TabsList className="auth__tabs" aria-label="Authentication mode">
            <TabsTrigger value="signin">Sign in</TabsTrigger>
            <TabsTrigger value="signup">Create account</TabsTrigger>
          </TabsList>
        </Tabs>

        <form className="auth__form" onSubmit={(event) => void submit(event)}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            hint={mode === 'signup' ? 'At least 8 characters.' : undefined}
            required
          />
          {mode === 'signup' ? (
            <TextField
              label="Organisation"
              value={organizationName}
              onChange={setOrganizationName}
              hint="Optional. One workspace per organisation; every project, test and run lives inside it."
              autoComplete="organization"
            />
          ) : null}

          {mode === 'signup' ? (
            <label className="signup-consent">
              <Checkbox checked={agreeTerms} onCheckedChange={(next) => setAgreeTerms(next === true)} required aria-label="Agree to Terms of Service and Privacy Policy" />
              <span>
                I agree to the{' '}
                <a href="/legal#terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                and{' '}
                <a href="/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </span>
            </label>
          ) : null}

          {error ? <ErrorNotice error={error} title={error instanceof ApiError ? 'Sign in failed' : 'Error'} /> : null}

          <Button type="submit" className="auth__submit" disabled={pending}>
            {pending ? 'Working…' : mode === 'signup' ? 'Create account' : 'Sign in'}
          </Button>
        </form>

        <p className="auth__alt">
          Already have a key? <code>JUSTLOOP_API_KEY=jlk_… npm run dev</code> or paste it into{' '}
          <code>localStorage</code> as <code>{API_KEY_STORAGE}</code>.
        </p>
      </div>
    </div>
  );
}

export function ApiKeyReveal({
  apiKey,
  scopes,
  onDone,
}: {
  apiKey: string;
  scopes: readonly string[];
  onDone: () => void;
}): JSX.Element {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="auth">
      <div className="auth__panel auth__panel--wide">
        <h1>Your API key</h1>
        <p className="notice notice--warn" role="alert">
          <strong>This is the only time it is shown.</strong> The server stores a hash; there is no way to
          read it back. Copy it now. If you lose it, mint another one from Settings.
        </p>
        <div className="secret">
          <code className="secret__value">{revealed ? apiKey : apiKey.replace(/.(?=.{6})/g, '•')}</code>
          <Button type="button" size="sm" variant="outline" onClick={() => setRevealed((v) => !v)}>
            {revealed ? 'Hide key' : 'Reveal key'}
          </Button>
          <CopyButton value={apiKey} label="Copy key" />
        </div>
        <p className="muted">
          Scopes: {scopes.length > 0 ? scopes.join(', ') : 'unknown (fetch /v1/auth/whoami to confirm)'}
        </p>
        <Button type="button" className="auth__submit" onClick={onDone}>
          Continue to dashboard
        </Button>
      </div>
    </div>
  );
}

function ServerIndicator(): JSX.Element {
  const { health, connection, error, lastCheckedAt } = useHealth();
  const { theme, toggle } = useTheme();

  const detail = (): string => {
    if (connection === 'offline') return error ?? 'no answer from /v1/health';
    if (!health) return 'checking…';
    const drivers = health.drivers
      .map((driver) => `${driver.name} ${driver.available ? 'available' : 'unavailable'}`)
      .join(' · ');
    return `${health.version} · ${drivers}`;
  };

  return (
    <div className={`auth__server auth__server--${connection}`}>
      <div className="auth__server-line">
        <span className="status__dot" aria-hidden="true" />
        <strong>
          {connection === 'online'
            ? `server ${health?.version ?? 'reachable'}`
            : connection === 'offline'
              ? 'server unreachable'
              : 'checking server'}
        </strong>
        <button type="button" className="btn btn--ghost btn--small" onClick={toggle}>
          {theme === 'dark' ? 'light theme' : 'dark theme'}
        </button>
      </div>
      <p className="muted">{detail()}</p>
      <p className="muted">
        {connection === 'offline'
          ? 'Start it with `npm run dev:server`, or point the Vite proxy elsewhere.'
          : `queues ${health?.queues.running ?? 0}/${health?.queues.concurrency ?? 0} running · checked ${lastCheckedAt ? new Date(lastCheckedAt).toLocaleTimeString() : 'never'}`}
      </p>
      {health?.status === 'degraded' ? (
        <p className="notice notice--warn">
          The server is degraded: a driver is unavailable, so frontend runs will not work. Backend runs are
          unaffected.
        </p>
      ) : null}
    </div>
  );
}